/**
 * Product Sensei Component
 * Expert PM mentor with multiple interaction modes
 */

import React, { useState, useEffect, useRef } from 'react';
import './AIMentor.css';
import { MessageCircle, Lightbulb, Target, BookOpen, X, Send, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { getMentorResponse } from '../../utils/api';

const MENTOR_MODES = [
  { id: 'guidance', name: 'Guidance', icon: Lightbulb, description: 'Get helpful advice when stuck' },
  { id: 'challenge', name: 'Challenge', icon: Target, description: 'Question your assumptions' },
  { id: 'review', name: 'Review', icon: MessageCircle, description: 'Get feedback on your work' },
  { id: 'framework', name: 'Framework', icon: BookOpen, description: 'Learn PM frameworks' },
];

const AIMentor = ({ isOpen, onClose, scenarioContext, initialMessage }) => {
  const [activeMode, setActiveMode] = useState('guidance');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const [chatContext, setChatContext] = useState(null);
  const hasAutoSentRef = useRef(false);
  const initialMessageRef = useRef(null);

  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Reset auto-send flag when panel closes
  useEffect(() => {
    if (!isOpen) {
      hasAutoSentRef.current = false;
      initialMessageRef.current = null;
      setMessages([]);
      setInput('');
    }
  }, [isOpen]);

  // Handle initial message from chat highlight - auto-send
  useEffect(() => {
    if (isOpen && initialMessage && !hasAutoSentRef.current && initialMessageRef.current !== initialMessage) {
      console.log('[AIMentor] Auto-sending initial message:', initialMessage);
      initialMessageRef.current = initialMessage;
      hasAutoSentRef.current = true;
      
      // Set input first
      setInput(initialMessage);
      
      // Then auto-send after ensuring state is updated
      const timer = setTimeout(() => {
        // Use the initialMessage directly since we just set it
        if (initialMessage && initialMessage.trim()) {
          console.log('[AIMentor] Sending message to API...');
          // Call handleSend directly with the message
          const sendMessage = async () => {
            const messageToSend = initialMessage.trim();
            if (!messageToSend) {
              console.log('[AIMentor] Message is empty, skipping');
              return;
            }

            const userMessage = {
              id: `msg-${Date.now()}`,
              type: 'user',
              text: messageToSend,
              timestamp: new Date().toISOString()
            };

            setMessages(prev => [...prev, userMessage]);
            setInput('');
            setLoading(true);

            try {
              console.log('[AIMentor] Calling getMentorResponse with:', { messageToSend, activeMode });
              const response = await getMentorResponse(messageToSend, activeMode, {
                scenarioContext: scenarioContext || {},
                companyContext: scenarioContext?.company || {},
                chatContext: chatContext || null
              });

              console.log('[AIMentor] Received response:', {
                responseLength: response.response?.length,
                responsePreview: response.response?.substring(0, 100) + '...',
                fullResponse: response.response
              });
              
              const mentorMessage = {
                id: `msg-${Date.now()}-response`,
                type: 'mentor',
                text: response.response,
                timestamp: response.timestamp
              };
              
              console.log('[AIMentor] Message text length:', mentorMessage.text?.length);

              setMessages(prev => [...prev, mentorMessage]);
              setChatContext(null);
            } catch (error) {
              console.error('[AIMentor] Error getting mentor response:', error);
              const errorMessage = {
                id: `msg-${Date.now()}-error`,
                type: 'error',
                text: 'Failed to get mentor response. Please check your API configuration.'
              };
              setMessages(prev => [...prev, errorMessage]);
              setChatContext(null);
            } finally {
              setLoading(false);
            }
          };
          
          sendMessage();
        }
      }, 400);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, initialMessage, activeMode, scenarioContext, chatContext]);

  const handleSend = async () => {
    const messageToSend = input.trim();
    if (!messageToSend || loading) return;

    const userMessage = {
      id: `msg-${Date.now()}`,
      type: 'user',
      text: messageToSend,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await getMentorResponse(messageToSend, activeMode, {
        scenarioContext: scenarioContext || {},
        companyContext: scenarioContext?.company || {},
        chatContext: chatContext || null
      });

      const mentorMessage = {
        id: `msg-${Date.now()}-response`,
        type: 'mentor',
        text: response.response,
        timestamp: response.timestamp
      };

      setMessages(prev => [...prev, mentorMessage]);
      // Clear chat context after sending
      setChatContext(null);
    } catch (error) {
      console.error('Error getting mentor response:', error);
      const errorMessage = {
        id: `msg-${Date.now()}-error`,
        type: 'error',
        text: 'Failed to get mentor response. Please check your API configuration.'
      };
      setMessages(prev => [...prev, errorMessage]);
      setChatContext(null);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="mentor-overlay" onClick={onClose}>
      <div className="mentor-panel" onClick={(e) => e.stopPropagation()}>
        <div className="mentor-header">
          <div className="mentor-title">
            <h2>Product Sensei</h2>
            <p className="mentor-credentials">
              15+ years at Google, Meta, and startups • $100M+ products shipped
            </p>
          </div>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="mentor-modes">
          {MENTOR_MODES.map(mode => {
            const Icon = mode.icon;
            return (
              <button
                key={mode.id}
                className={`mode-button ${activeMode === mode.id ? 'active' : ''}`}
                onClick={() => setActiveMode(mode.id)}
                title={mode.description}
              >
                <Icon size={18} />
                <span>{mode.name}</span>
              </button>
            );
          })}
        </div>

        <div className="mentor-messages">
          {messages.length === 0 && (
            <div className="empty-state">
              <p>Ask me anything about product management!</p>
              <p className="hint">I can guide you, challenge your thinking, review your work, or teach frameworks.</p>
            </div>
          )}
          
          {messages.map(message => (
            <div
              key={message.id}
              className={`mentor-message ${message.type === 'user' ? 'user' : 'mentor'}`}
            >
              <div className="message-content">
                {message.type === 'mentor' ? (
                  <ReactMarkdown>{message.text}</ReactMarkdown>
                ) : (
                  <p>{message.text}</p>
                )}
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="mentor-message mentor loading">
              <div className="message-content">
                <Loader2 size={16} className="spinner" />
                <span>Thinking...</span>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        <div className="mentor-input-container">
          <textarea
            className="mentor-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={`Ask in ${MENTOR_MODES.find(m => m.id === activeMode)?.name} mode...`}
            disabled={loading}
            rows={2}
          />
          <button
            className="mentor-send-button"
            onClick={handleSend}
            disabled={loading || !input.trim()}
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIMentor;



